/**
 * Mails aus dem NEUEN Outlook für Windows per Drag & Drop ablegen (User
 * 03.11.2026: „Mails lassen sich aus dem neuen Outlook nicht mehr in KMVS
 * ziehen“).
 *
 * Das klassische Outlook legt beim Ziehen eine `.msg`-Datei in den Zug — die
 * nimmt KMVS wie jede Datei an. Das neue Outlook (technisch ein Web-Client wie
 * OWA) liefert KEINE Datei, sondern nur den String-Typ `maillistrow` mit einem
 * JSON (gemessen in Edge 156, gekürzt):
 *
 * ```json
 * { "itemType": "maillistrow", "rowKeys": ["…"], "tableListViewType": 1,
 *   "subjects": ["<Betreff>"], "haveIRM": [false], "sizes": [55375],
 *   "mailboxInfos": [{ "mailboxSmtpAddress": "user@firma.de", "type": "UserMailbox", … }],
 *   "latestItemIds": ["AAkALg…"], "itemIds": ["AAkALg…"], "nodeIds": [],
 *   "itemData": { "mailboxInfo": { … } } }
 * ```
 *
 * Mehrere Mails → die Arrays laufen Index-parallel. Ist `mailboxInfos`
 * kürzer, gilt `itemData.mailboxInfo` (dann kommen alle aus einem Postfach).
 *
 * **Unterhaltungsansicht:** Eine Zeile kann eine ganze Unterhaltung sein
 * (`tableListViewType` ≠ 1, `nodeIds`). Dann nimmt KMVS die jeweils NEUESTE
 * Mail der Unterhaltung (`latestItemIds[i]`) — so, wie Outlook beim Ziehen
 * einer Unterhaltung auch nur deren Kopf anzeigt. In der Nachrichtenansicht
 * (`tableListViewType` = 1) gilt `itemIds[i]`, sonst ersatzweise
 * `latestItemIds[i]`.
 *
 * Die `itemIds` sind EWS-Ids. Der Server übersetzt sie in Graph-Ids
 * (`POST /mail/outlook-zug`) und liefert je Mail denselben Snapshot wie ein
 * Zug aus der KMVS-Mailliste (`ZIEHTYP_POSTFACH_MAIL`). Ab da geht die Mail
 * exakt denselben Weg — Anhänge-Frage, Platzhalter, Meldungen.
 *
 * Gelesen wird SYNCHRON im `drop` ({@link ausNeuemOutlook}); die Übersetzung
 * kommt danach.
 */
import { apiUrl } from "../../utils/api";
import { hinweisen, type HinweisOptionen } from "../redesign/bestaetigen";
import { neuesOutlookTyp } from "../vorgang/detail/todoDrop";
import type { AblageErgebnis, AblageZug } from "./postfachAblage";
import type { PostfachMailZug } from "./postfachZug";

export { ZIEHTYP_NEUES_OUTLOOK } from "../vorgang/detail/todoDrop";

/** Höchstzahl Mails je Zug — wie der Server (`OUTLOOK_ZUG_MAX`). */
export const NEUES_OUTLOOK_MAX = 25;

export type NeuesOutlookMail = {
    mailbox: string;
    /** EWS-Id der Mail (bei einer Unterhaltung: ihrer neuesten Mail). */
    itemId: string;
    betreff: string | null;
    /** Mit Rechteverwaltung (IRM) geschützt — Graph liefert dann nur eine verschlüsselte Hülle. */
    irm: boolean;
};

export type NeuesOutlookZug = {
    mails: NeuesOutlookMail[];
    /** Zeilen, zu denen sich kein Postfach oder keine Id fand. */
    unlesbar: number;
};

/**
 * Kommt der Zug aus dem neuen Outlook? Liegt zusätzlich eine Datei an
 * (klassisches Outlook, andere Programme), gewinnt die Datei — der bewährte
 * `.msg`-Weg bleibt unverändert.
 */
export function istNeuesOutlookZug(typen: readonly string[] | undefined): boolean {
    const liste = typen ?? [];
    return !!neuesOutlookTyp(liste) && !liste.includes("Files");
}

const textOderNull = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

const smtpVon = (info: unknown): string | null => {
    if (!info || typeof info !== "object") return null;
    const i = info as Record<string, unknown>;
    const adresse = textOderNull(i.mailboxSmtpAddress) ?? textOderNull(i.userIdentity);
    return adresse && adresse.includes("@") ? adresse.toLowerCase() : null;
};

const liste = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

/** Das `maillistrow`-JSON lesen; Fremdes oder Kaputtes wird `null`. */
export function leseNeuesOutlookZug(roh: string | null | undefined): NeuesOutlookZug | null {
    if (!roh) return null;
    let daten: unknown;
    try {
        daten = JSON.parse(roh);
    } catch {
        return null;
    }
    if (!daten || typeof daten !== "object" || Array.isArray(daten)) return null;
    const d = daten as Record<string, unknown>;
    if (d.itemType != null && !(typeof d.itemType === "string" && /maillist/i.test(d.itemType))) return null;

    const itemIds = liste(d.itemIds);
    const latest = liste(d.latestItemIds);
    const infos = liste(d.mailboxInfos);
    const subjects = liste(d.subjects);
    const irm = liste(d.haveIRM);
    const ersatzPostfach =
        smtpVon((d.itemData as Record<string, unknown> | undefined)?.mailboxInfo) ?? smtpVon(infos[0]);
    // 1 = Nachrichtenansicht; alles andere behandeln wie Unterhaltungen.
    const unterhaltung = d.tableListViewType !== 1 && d.tableListViewType !== "1";

    const anzahl = Math.max(itemIds.length, latest.length);
    const mails: NeuesOutlookMail[] = [];
    let unlesbar = 0;
    for (let i = 0; i < anzahl; i++) {
        const einzel = textOderNull(itemIds[i]);
        const neueste = textOderNull(latest[i]);
        const itemId = unterhaltung ? neueste ?? einzel : einzel ?? neueste;
        const mailbox = smtpVon(infos[i]) ?? ersatzPostfach;
        if (!itemId || !mailbox) {
            unlesbar += 1;
            continue;
        }
        mails.push({ mailbox, itemId, betreff: textOderNull(subjects[i]), irm: irm[i] === true });
    }
    return mails.length > 0 || unlesbar > 0 ? { mails, unlesbar } : null;
}

/** Den Zug beim `drop` lesen — synchron, solange der Browser ihn noch hergibt. */
export function ausNeuemOutlook(dt: DataTransfer | null | undefined): NeuesOutlookZug | null {
    const typen = Array.from(dt?.types ?? []);
    if (!dt || !istNeuesOutlookZug(typen)) return null;
    try {
        return leseNeuesOutlookZug(dt.getData(neuesOutlookTyp(typen)!));
    } catch {
        return null;
    }
}

type ServerErgebnis =
    | { status: "ok"; mail: PostfachMailZug }
    | { status: "kein_zugriff" | "nicht_gefunden" | "fehler"; mailbox?: string; meldung?: string };

export type Uebersetzung = {
    /** Fertige Postfach-Züge, Reihenfolge wie im Outlook-Zug. */
    zuege: PostfachMailZug[];
    /** Sätze für den Nutzer — was nicht ging und warum. */
    probleme: string[];
};

const name = (m: NeuesOutlookMail) => `„${m.betreff || "Ohne Betreff"}“`;

/**
 * EWS-Ids über den Server in Postfach-Züge übersetzen. Wirft nie — alles, was
 * nicht ging, steht in `probleme`.
 */
export async function uebersetzeNeuesOutlookZug(
    zug: NeuesOutlookZug,
    abruf: typeof fetch = (...a) => fetch(...a),
): Promise<Uebersetzung> {
    const probleme: string[] = [];
    if (zug.unlesbar > 0) {
        probleme.push(
            zug.unlesbar === 1
                ? "Bei einer Mail hat Outlook nicht mitgeliefert, aus welchem Postfach sie stammt."
                : `Bei ${zug.unlesbar} Mails hat Outlook nicht mitgeliefert, aus welchem Postfach sie stammen.`,
        );
    }
    const geschuetzt = zug.mails.filter((m) => m.irm);
    for (const m of geschuetzt) {
        probleme.push(
            `Die Mail ${name(m)} ist geschützt (Rechteverwaltung/„Vertraulich“). KMVS kann sie nicht lesbar ablegen – bitte in Outlook den Schutz entfernen lassen oder den Inhalt anders weitergeben.`,
        );
    }
    let offen = zug.mails.filter((m) => !m.irm);
    if (offen.length > NEUES_OUTLOOK_MAX) {
        probleme.push(
            `Es lassen sich höchstens ${NEUES_OUTLOOK_MAX} Mails auf einmal ablegen – die ersten ${NEUES_OUTLOOK_MAX} werden abgelegt, bitte den Rest danach ziehen.`,
        );
        offen = offen.slice(0, NEUES_OUTLOOK_MAX);
    }
    if (offen.length === 0) return { zuege: [], probleme };

    let ergebnisse: ServerErgebnis[];
    try {
        const res = await abruf(apiUrl("mail/outlook-zug"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ mails: offen.map((m) => ({ mailbox: m.mailbox, itemId: m.itemId })) }),
        });
        if (!res.ok) throw new Error(String(res.status));
        const daten = (await res.json()) as { ergebnisse?: ServerErgebnis[] };
        ergebnisse = Array.isArray(daten?.ergebnisse) ? daten.ergebnisse : [];
    } catch {
        probleme.push(
            offen.length === 1
                ? `Die Mail ${name(offen[0])} aus Outlook konnte gerade nicht abgerufen werden. Bitte gleich noch einmal ziehen.`
                : "Die Mails aus Outlook konnten gerade nicht abgerufen werden. Bitte gleich noch einmal ziehen.",
        );
        return { zuege: [], probleme };
    }

    const zuege: PostfachMailZug[] = [];
    offen.forEach((m, i) => {
        const e = ergebnisse[i];
        if (e?.status === "ok" && e.mail?.mailbox && e.mail?.messageId) {
            // Betreff aus Outlook, falls Graph keinen hat (leerer Betreff bleibt leer).
            zuege.push({ ...e.mail, subject: e.mail.subject ?? m.betreff ?? null });
            return;
        }
        const grund = e && e.status !== "ok" && e.meldung ? e.meldung : "Sie konnte gerade nicht abgerufen werden.";
        probleme.push(`${name(m)}: ${grund}`);
    });
    return { zuege, probleme };
}

const verbinde = (liste: AblageErgebnis[]): AblageErgebnis | null =>
    liste.length === 0
        ? null
        : liste.reduce((a, b) => ({
              abgelegt: a.abgelegt + b.abgelegt,
              vorhanden: a.vorhanden + b.vorhanden,
              fehlgeschlagen: [...a.fehlgeschlagen, ...b.fehlgeschlagen],
              dokumentIds: [...(a.dokumentIds ?? []), ...(b.dokumentIds ?? [])],
              laeuft: !!a.laeuft || !!b.laeuft,
              meldung: `${a.meldung} ${b.meldung}`.trim(),
          }));

/**
 * Einen Outlook-Zug ablegen: übersetzen, dann jede Mail über `ablegen` (den
 * Weg des Ziels — mit oder ohne Anhänge-Frage), nacheinander, damit die
 * Fragen nicht übereinander erscheinen. Was nicht ging, sagt EIN Hinweis.
 * Ergebnis: alle Ablagen zusammengefasst; `null`, wenn nichts abgelegt wurde
 * (abgebrochen oder alles gescheitert).
 */
export async function legeNeuesOutlookAb(
    zug: NeuesOutlookZug,
    ablegen: (zug: AblageZug) => Promise<AblageErgebnis | null>,
    optionen: {
        abruf?: typeof fetch;
        melden?: (o: HinweisOptionen) => Promise<void>;
    } = {},
): Promise<AblageErgebnis | null> {
    const melden = optionen.melden ?? hinweisen;
    const { zuege, probleme } = await uebersetzeNeuesOutlookZug(zug, optionen.abruf);
    const ergebnisse: AblageErgebnis[] = [];
    for (const mail of zuege) {
        const erg = await ablegen({ art: "postfachMail", mail });
        if (erg) ergebnisse.push(erg);
    }
    // Erst danach: der Hinweis soll nicht über einer Anhänge-Frage liegen.
    if (probleme.length > 0) {
        await melden({
            titel: zuege.length > 0 ? "Nicht alle Mails aus Outlook abgelegt" : "Mail aus Outlook nicht abgelegt",
            text: probleme.join("\n\n"),
            ton: "danger",
        });
    }
    return verbinde(ergebnisse);
}
